import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { CHART, OpsChart, pairs } from "../components/OpsChart";
import { opsFetch } from "../lib/api";

type GrowthRes = {
  data: {
    days: number;
    events: { total: number; counts: Record<string, number> };
    intents: {
      total: number;
      by_scenario: Record<string, number>;
      by_city: Record<string, number>;
    };
  };
};

type AffRes = {
  data: {
    total: number;
    by_partner: Record<string, number>;
    by_city: Record<string, number>;
    by_placement: Record<string, number>;
    by_product: Record<string, number>;
  };
};

type Board = {
  data: {
    pv: number;
    uv: number;
    clicks: number;
    conversion: number;
    series: Array<{ date: string; pv: number; uv: number; clicks: number }>;
  };
};

const text = { fontFamily: "Plus Jakarta Sans, PingFang SC, sans-serif", color: CHART.ink };

export function GrowthPage() {
  const [days, setDays] = useState(7);
  const [tab, setTab] = useState<"events" | "affiliate">("events");
  const q = useQuery({
    queryKey: ["ops-growth", days],
    queryFn: () => opsFetch<GrowthRes>(`/api/ops/growth/summary?days=${days}`),
  });
  const aff = useQuery({
    queryKey: ["ops-aff-clicks", days],
    queryFn: () => opsFetch<AffRes>(`/api/ops/growth/affiliate-clicks?days=${days}`),
  });
  const board = useQuery({
    queryKey: ["ops-settlement-chart", days],
    queryFn: () => opsFetch<Board>(`/api/ops/settlement?days=${days}`),
  });
  const series = board.data?.data.series ?? [];
  const dates = series.map((row) => row.date.slice(5));

  const trend = useMemo(
    () => ({
      textStyle: text,
      tooltip: { trigger: "axis" as const },
      legend: { data: ["PV", "UV", "Clicks"], textStyle: { color: CHART.muted } },
      grid: { left: 36, right: 12, top: 36, bottom: 28 },
      xAxis: { type: "category" as const, data: dates, axisLabel: { color: CHART.muted } },
      yAxis: { type: "value" as const, axisLabel: { color: CHART.muted }, splitLine: { lineStyle: { color: "rgba(112,92,255,0.08)" } } },
      series: [
        { name: "PV", type: "line" as const, smooth: true, data: series.map((row) => row.pv), itemStyle: { color: CHART.accent } },
        { name: "UV", type: "line" as const, smooth: true, data: series.map((row) => row.uv), itemStyle: { color: CHART.orange } },
        { name: "Clicks", type: "line" as const, smooth: true, data: series.map((row) => row.clicks), itemStyle: { color: CHART.green } },
      ],
    }),
    [dates, series],
  );

  const funnel = useMemo(() => {
    const data = board.data?.data;
    return {
      textStyle: text,
      tooltip: { trigger: "item" as const },
      series: [
        {
          type: "funnel" as const,
          left: "8%",
          width: "84%",
          label: { color: CHART.ink },
          data: [
            { name: "PV", value: data?.pv ?? 0 },
            { name: "UV", value: data?.uv ?? 0 },
            { name: "Clicks", value: data?.clicks ?? 0 },
          ],
          itemStyle: { borderColor: "#fff", borderWidth: 2 },
          color: [CHART.accent, CHART.orange, CHART.green],
        },
      ],
    };
  }, [board.data]);

  const events = q.data?.data.events.counts ?? {};
  const eventPie = useMemo(
    () => ({
      textStyle: text,
      tooltip: { trigger: "item" as const },
      series: [
        {
          type: "pie" as const,
          radius: ["46%", "72%"],
          data: pairs(events).map(([name, value]) => ({ name, value })),
          label: { color: CHART.muted },
          color: [CHART.accent, CHART.orange, CHART.pink, CHART.green, "#8b74ff"],
        },
      ],
    }),
    [events],
  );
  const cityBar = barOption("Intents by city", q.data?.data.intents.by_city ?? {});
  const partnerBar = barOption("Clicks by partner", aff.data?.data.by_partner ?? {});
  const placeBar = barOption("Clicks by placement", aff.data?.data.by_placement ?? {});
  const productBar = barOption("Clicks by product", aff.data?.data.by_product ?? {});

  return (
    <>
      <div className="ops-toolbar">
        <div className="ops-tabs">
          <button className={tab === "events" ? "ops-tab ops-tab--active" : "ops-tab"} onClick={() => setTab("events")} type="button">
            Traffic
          </button>
          <button className={tab === "affiliate" ? "ops-tab ops-tab--active" : "ops-tab"} onClick={() => setTab("affiliate")} type="button">
            Conversion
          </button>
        </div>
        <select className="ops-input" onChange={(e) => setDays(Number(e.target.value))} value={days}>
          {[7, 14, 30, 90].map((d) => (
            <option key={d} value={d}>{d} days</option>
          ))}
        </select>
      </div>
      {board.data ? (
        <p className="ops-callout ops-callout--ok">
          {board.data.data.clicks === 0
            ? "People are landing, but nobody clicked out to a partner in this window. Traffic is the drop. Conversion is empty until a click happens."
            : `${board.data.data.uv} visitors produced ${board.data.data.clicks} partner clicks. Traffic is the drop. Conversion is which product took the click.`}
        </p>
      ) : null}
      {board.data ? (
        <div className="ops-card-grid">
          <div className="ops-card"><p className="ops-card__label">PV</p><p className="ops-card__value">{board.data.data.pv}</p></div>
          <div className="ops-card"><p className="ops-card__label">UV</p><p className="ops-card__value">{board.data.data.uv}</p></div>
          <div className="ops-card"><p className="ops-card__label">Clicks</p><p className="ops-card__value">{board.data.data.clicks}</p></div>
          <div className="ops-card"><p className="ops-card__label">Click / UV</p><p className="ops-card__value">{Math.round((board.data.data.conversion || 0) * 1000) / 10}%</p></div>
        </div>
      ) : null}
      <div className="ops-panel">
        <h3 className="ops-panel__title">PV, UV, and clicks</h3>
        <OpsChart height={300} option={trend} />
      </div>
      {tab === "events" ? (
        <>
          <div className="ops-chart-grid">
            <div className="ops-panel"><h3 className="ops-panel__title">Conversion funnel</h3><OpsChart option={funnel} /></div>
            <div className="ops-panel"><h3 className="ops-panel__title">Event mix</h3><OpsChart option={eventPie} /></div>
          </div>
          <div className="ops-panel">
            <h3 className="ops-panel__title">Intent cities</h3>
            <OpsChart option={cityBar} />
          </div>
        </>
      ) : (
        <>
          <div className="ops-chart-grid">
            <div className="ops-panel"><h3 className="ops-panel__title">Partners</h3><OpsChart option={partnerBar} /></div>
            <div className="ops-panel"><h3 className="ops-panel__title">Placements</h3><OpsChart option={placeBar} /></div>
          </div>
          <div className="ops-panel">
            <h3 className="ops-panel__title">Products</h3>
            <OpsChart option={productBar} />
          </div>
        </>
      )}
    </>
  );
}

function barOption(title: string, counts: Record<string, number>) {
  const rows = pairs(counts);
  return {
    textStyle: text,
    tooltip: { trigger: "axis" as const },
    grid: { left: 88, right: 16, top: 12, bottom: 24 },
    xAxis: { type: "value" as const, axisLabel: { color: CHART.muted }, splitLine: { lineStyle: { color: "rgba(112,92,255,0.08)" } } },
    yAxis: { type: "category" as const, data: rows.map(([name]) => name).reverse(), axisLabel: { color: CHART.muted, width: 72, overflow: "truncate" as const } },
    series: [{ name: title, type: "bar" as const, data: rows.map(([, value]) => value).reverse(), itemStyle: { color: CHART.accent, borderRadius: [0, 8, 8, 0] }, barMaxWidth: 18 }],
  };
}
